import { Header } from "@/components/header";
import { trpc } from "@/lib/trpc";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate, Outlet, redirect, useMatch } from "@tanstack/react-router";
import z from "zod";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
  head: () => ({
    meta: [{ title: "Papier" }],
  }),
  validateSearch: z.object({
    plan: z.literal("pro").optional(),
  }),
  beforeLoad: async ({ context: { queryClient, authOptions }, location, search: { plan } }) => {
    const [session, hasOrgs] = await Promise.all([
      queryClient.fetchQuery(authOptions.session),
      queryClient.fetchQuery(authOptions.hasOrganizations),
    ]);

    if (!session) {
      throw redirect({
        to: "/auth/$authView",
        params: { authView: "sign-in" },
        search: {
          redirectTo: location.pathname !== "/" ? location.pathname : undefined,
          plan,
        },
      });
    }

    if (!hasOrgs || !session.session.activeOrganizationId) {
      throw redirect({
        to: "/auth/$authView",
        params: { authView: "organization" },
        search: {
          redirectTo: location.pathname !== "/" ? location.pathname : undefined,
          plan,
        },
      });
    }

    if (location.pathname !== "/organization/billing" && plan) {
      throw redirect({
        to: "/organization/billing",
        search: { plan },
      });
    }
  },
});

function AppLayout() {
  const editorMatch = useMatch({
    from: "/_app/projects/$projectSlug/templates/$templateSlug/edit",
    shouldThrow: false,
    select: (match) => match.id,
  });

  const { authOptions } = Route.useRouteContext();
  const { data: session } = useSuspenseQuery(authOptions.session);

  useSuspenseQuery(
    trpc.authTrpc.ensureCdnAccess.queryOptions(undefined, {
      staleTime: 1000 * 60 * 50, // Refresh 10 minutes before expiry (token lasts 1 hour)
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }),
  );

  if (!session) {
    return <Navigate to="/auth/$authView" params={{ authView: "sign-in" }} />;
  }

  return (
    <div className="dark:bg-background bg-content-background flex max-h-[100svh] min-h-[100svh] w-full flex-col">
      {!editorMatch && <Header />}
      <div className="flex flex-1 overflow-hidden">
        <div className="dark:bg-content-background bg-background relative m-2 mt-0 flex flex-1 flex-col overflow-y-auto rounded-lg border">
          {/* Add p-4 to any content container */}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
